import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
    Briefcase, BookOpen, Phone, Mail, MapPin, Code, UserCheck,
    Award, Users, Target, TrendingUp, CheckCircle, Star, 
    Globe, Clock, Shield, ArrowRight, GraduationCap, Building, 
    Heart, Lightbulb, Trophy, Zap, Play, BarChart3, X
} from 'lucide-react';

// Import success stories images
import truptiImg from '../assets/success stories/TruptiNagar_Microsoft.png';
import sachinImg from '../assets/success stories/SachinTomar_CityBank.png';
import shubhamJainImg from '../assets/success stories/ShubhamJain_NTT Data.png';
import neerajImg from '../assets/success stories/NeerajSharma_HCL.png';
import rakshitImg from '../assets/success stories/RakshitBhatia_JDSoftware.png';
import rishiVyasImage from '../assets/Rishi_Vyas_DataAnalysis.jpg';
import { motion } from 'framer-motion';
import CertificateModal from '../components/CertificateModal';

const StatCard = ({ icon: Icon, number, label, description, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:scale-105"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
            <Icon className="w-8 h-8 text-white" />
        </div>
        <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            {number}
        </div>
        <div className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">{label}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{description}</div>
    </motion.div>
);

const FeatureCard = ({ icon: Icon, title, description, gradient, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className={`w-16 h-16 ${gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
    </motion.div>
);

const MentorCard = ({ name, title, points, quote, experience, students, achievements = [], delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 h-full flex flex-col group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-start space-x-6 mb-6">
            <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex-shrink-0 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                    <UserCheck className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                </div>
            </div>
            <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                    {name}
                </h3>
                <p className="text-primary-600 dark:text-primary-400 font-semibold text-lg">{title}</p>
                <div className="flex items-center mt-2">
                    {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                    <span className="text-sm text-gray-500 ml-2">Expert Level</span>
                </div>
            </div>
        </div>

        <div className="flex flex-col flex-grow">
            <div className="flex flex-wrap gap-2 mb-6">
                {points.map((point, i) => (
                    <span key={i} className="bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/50 dark:to-primary-800/50 text-primary-800 dark:text-primary-200 text-sm font-medium px-3 py-1.5 rounded-full border border-primary-200 dark:border-primary-700">
                        {point}
                    </span>
                ))}
            </div>

            <blockquote className="border-l-4 border-primary-500 pl-6 py-4 my-6 italic text-gray-700 dark:text-gray-300 bg-gradient-to-r from-primary-50 to-transparent dark:from-primary-900/20 dark:to-transparent rounded-r-xl relative">
                <div className="absolute top-2 left-2 text-primary-500 text-2xl">"</div>
                <div className="ml-6">{quote}</div>
                <div className="absolute bottom-2 right-4 text-primary-500 text-2xl">"</div>
            </blockquote>

            {achievements.length > 0 && (
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Trophy className="w-4 h-4 mr-2 text-yellow-500" />
                        Key Achievements
                    </h4>
                    <ul className="space-y-2">
                        {achievements.slice(0, 3).map((achievement, i) => (
                            <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                                <CheckCircle className="w-4 h-4 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                                {achievement}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* <div className="grid grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{experience}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Experience</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{students}</p>
                    <p className="text-sm text-green-700 dark:text-green-300 font-medium">Students Trained</p>
                </div>
            </div> */}
        </div>
    </motion.div>
);

const ContactInfoCard = ({ title, address, phone, email, icon, hours, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 h-full group hover:-translate-y-1"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-center text-2xl font-bold text-gray-900 dark:text-white mb-8 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
            {icon}
            </div>
            <h3>{title}</h3>
        </div>
        <div className="space-y-6 text-gray-600 dark:text-gray-300">
            <div className="flex items-start group/item hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200">
                <MapPin className="w-6 h-6 mr-4 mt-1 text-primary-500 flex-shrink-0 group-hover/item:scale-110 transition-transform duration-200"/>
                <span className="leading-relaxed">{address}</span>
            </div>
            <div className="flex items-center group/item hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200">
                <Phone className="w-5 h-5 mr-4 text-primary-500 group-hover/item:scale-110 transition-transform duration-200"/>
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-primary-500 transition-colors font-medium">
                    {phone}
                </a>
            </div>
            <div className="flex items-center group/item hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200">
                <Mail className="w-5 h-5 mr-4 text-primary-500 group-hover/item:scale-110 transition-transform duration-200"/>
                <a href={`mailto:${email}`} className="hover:text-primary-500 transition-colors font-medium">
                    {email}
                </a>
            </div>
            {hours && (
                <div className="flex items-center group/item hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200">
                    <Clock className="w-5 h-5 mr-4 text-primary-500 group-hover/item:scale-110 transition-transform duration-200"/>
                    <span className="font-medium">{hours}</span>
                </div>
            )}
        </div>
    </motion.div>
);

const TestimonialCard = ({ name, role, company, testimonial, rating, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-4 group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-bold text-lg">{name.charAt(0)}</span>
            </div>
            <div>
                <h4 className="font-bold text-gray-900 dark:text-white text-lg">{name}</h4>
                <p className="text-primary-600 dark:text-primary-400 font-medium">{role}</p>
                <p className="text-sm text-gray-500">{company}</p>
            </div>
        </div>
        <div className="flex items-center mb-4">
            {[...Array(rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
            ))}
    </div>
        <blockquote className="text-gray-700 dark:text-gray-300 italic leading-relaxed">
            "{testimonial}"
        </blockquote>
    </motion.div>
);

const WhyOsop = () => {
    const [videoModal, setVideoModal] = useState({ isOpen: false, videoUrl: '', studentName: '', company: '' });
    const [showAllVideos, setShowAllVideos] = useState(false);
    const [visiblePlacements, setVisiblePlacements] = useState(15);
    const [certificateModal, setCertificateModal] = useState({ isOpen: false });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Function to get YouTube thumbnail URL
    const getYouTubeThumbnail = useCallback((videoUrl) => {
        if (videoUrl === "#") return null;
        const videoId = videoUrl.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
        return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }, []);

    // Video testimonials data
    const videoTestimonials = useMemo(() => [
        {
            name: "Trupti Nagar Jain",
            company: "Microsoft, USA",
            videoUrl: "https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK",
            thumbnail: getYouTubeThumbnail("https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK")
        },
        {
            name: "Sachin Tomar",
            company: "City Bank, Pune",
            videoUrl: "https://www.youtube.com/watch?v=EZROGHX2TCY",
            thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=EZROGHX2TCY")
        },
        {
            name: "Anshul Gupta",
            company: "Wipro, Mumbai",
            videoUrl: "https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s",
            thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s")
        },
        {
            name: "Neeraj Sharma",
            company: "HCL, Netherland",
            videoUrl: "https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT",
            thumbnail: getYouTubeThumbnail("https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT")
        },
        {
            name: "Shubham Jain",
            company: "NTT Data, USA",
            videoUrl: "https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul",
            thumbnail: getYouTubeThumbnail("https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul")
        },
        {
            name: "Rakshit Bhatia",
            company: "JD Software, Banglore",
            videoUrl: "https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx",
            thumbnail: getYouTubeThumbnail("https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx")
        },
        {
            name: "Shiva Jain",
            company: "TCS, Indore",
            videoUrl: "https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA",
            thumbnail: getYouTubeThumbnail("https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA")
        }
    ], [getYouTubeThumbnail]);

    // Function to open video modal
    const openVideoModal = useCallback((testimonial) => {
        if (testimonial.videoUrl !== "#") {
            // Extract YouTube video ID and create embed URL
            const videoId = testimonial.videoUrl.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
            const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&controls=1&autoplay=0&mute=0` : testimonial.videoUrl;
            
            setVideoModal({
                isOpen: true,
                videoUrl: embedUrl,
                studentName: testimonial.name,
                company: testimonial.company
            });
        }
    }, []);

    const closeVideoModal = useCallback(() => {
        setVideoModal({ isOpen: false, videoUrl: '', studentName: '', company: '' });
    }, []);

    // Certificate modal functions
    const openCertificateModal = useCallback(() => {
        setCertificateModal({ isOpen: true });
    }, []);

    const closeCertificateModal = useCallback(() => {
        setCertificateModal({ isOpen: false });
    }, []);

    // Placements Data
    const placements = [
        {
            name: "Tanwant Singh",
            role: "Java Developer",
            company: "WorldPay",
            image: require('../assets/placements/Tanwant-Singh-WorldPay.jpg')
        },
        {
            name: "Vasu Tiwari",
            role: "Software Developer",
            company: "Innoeye",
            image: require('../assets/placements/Vasu-Tiwari-Innoeye.jpg')
        },
        {
            name: "Shubham Rathore",
            role: "Software Engineer",
            company: "Hotwax",
            image: require('../assets/placements/Shubham-Rathore.jpg')
        },
        {
            name: "Shiva Jain",
            role: "Java Developer",
            company: "Hotwax",
            image: require('../assets/placements/Shiva-Jain-Hotwax.jpg')
        },
        {
            name: "Nilesh Goyal",
            role: "Software Developer",
            company: "Hotwax",
            image: require('../assets/placements/Nilesh-Goyal-Hotwax.jpg')
        },
        {
            name: "Niket Thada",
            role: "Full Stack Developer",
            company: "Hotwax",
            image: require('../assets/placements/Niket-Thada-Hotwax.jpg')
        },
        {
            name: "Neha Parsai",
            role: "Software Engineer",
            company: "Synechron",
            image: require('../assets/placements/Neha-Parsai-Synechrone (1).jpg')
        },
        {
            name: "Manish Singh",
            role: "Java Developer",
            company: "New Con Infosystems",
            image: require('../assets/placements/Manish-Singh-New-Con-infosystems.jpg')
        },
        {
            name: "Manali Jain",
            role: "Software Developer",
            company: "Diaspark",
            image: require('../assets/placements/Manali-Jain-Diaspark (1).jpg')
        },
        {
            name: "Lokesh Sharma",
            role: "Java Developer",
            company: "Infobeans",
            image: require('../assets/placements/Lokesh-Sharma-infobeans (1).jpg')
        },
        {
            name: "Lkshyajeet Singh",
            role: "Software Developer",
            company: "Yash Technology",
            image: require('../assets/placements/Lkshyajeet-Singh-Yash-technology.jpg')
        },
        {
            name: "Krishna Patel",
            role: "Web Developer",
            company: "Firstlogic",
            image: require('../assets/placements/Krishna-Patel-Firstlogic.jpg')
        },
        {
            name: "IndraJeet Singh",
            role: "Software Engineer",
            company: "Infobeans",
            image: require('../assets/placements/IndraJeet-Singh-Infobeans.jpg')
        },
        {
            name: "Hemant Jhanwar",
            role: "Java Developer",
            company: "Diaspark",
            image: require('../assets/placements/Hemant-Jhanwar-Diaspark.jpg')
        },
        {
            name: "Hardik Jain",
            role: "Software Developer",
            company: "HVantage",
            image: require('../assets/placements/Hardik-Jain-New-Con.jpg')
        },
        {
            name: "Ekta Sharma",
            role: "Full Stack Developer",
            company: "Synechron",
            image: require('../assets/placements/Ekta-Sharma--Synechrome.jpg')
        },
        {
            name: "Aprna Katare",
            role: "Software Engineer",
            company: "Synechron",
            image: require('../assets/placements/Aprna-Katare-Synechrone.jpg')
        },
        {
            name: "Ankush Shukla",
            role: "Java Development",
            company: "Synechron",
            image: require('../assets/placements/Ankush-Shukla-Synechrome.jpg')
        },
        {
            name: "Ankit Joshi",
            role: "Software Developer",
            company: "Hotwax",
            image: require('../assets/placements/Ankit-Joshi-Hotwax.jpg')
        },
        {
            name: "Ajay Riyar",
            role: "Java Developer",
            company: "WorldPay",
            image: require('../assets/placements/Ajay-Riyar-Worldpay.jpg')
        }
    ];

    // Success Stories Data
    const successStories = [
        {
            name: "Trupti Nagar",
            role: "Software Engineer",
            company: "Microsoft, USA",
            image: truptiImg || '/src/assets/success stories/TruptiNagar_Microsoft.png'
        },
        {
            name: "Sachin Tomar",
            role: "Software Developer",
            company: "City Bank",
            image: sachinImg || '/src/assets/success stories/SachinTomar_CityBank.png'
        },
        {
            name: "Shubham Jain",
            role: "Software Engineer",
            company: "NTT Data",
            image: shubhamJainImg || '/src/assets/success stories/ShubhamJain_NTT Data.png'
        },
        {
            name: "Neeraj Sharma",
            role: "Software Developer",
            company: "HCL, Netherland",
            image: neerajImg || '/src/assets/success stories/NeerajSharma_HCL.png'
        },
        {
            name: "Rakshit Bhatia",
            role: "Software Engineer",
            company: "JD Software",
            image: rakshitImg || '/src/assets/success stories/RakshitBhatia_JDSoftware.png'
        }
    ];

    const loadMore = () => {
        setVisiblePlacements(prev => Math.min(prev + 15, placements.length));
    };

    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerContainer = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 pt-20 overflow-hidden">
            {/* Enhanced Hero Section */}
            <section className="relative min-h-screen flex items-center">
                <div className="absolute inset-0 bg-gradient-professional opacity-95"></div>
                <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
                
                <div className="container-max relative z-10 py-20">
                    <div className="text-center text-white">
                        <motion.div
                            className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8"
                            {...fadeInUp}
                        >
                            <Award className="w-5 h-5 mr-2 text-yellow-400" />
                            <span className="text-sm font-medium">India's Premium Programming Institute</span>
                        </motion.div>

                    <motion.h1 
                            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
                            initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                    >
                            Why Choose{' '}
                            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                                OSOPCoding
                            </span>
                            ?
                    </motion.h1>

                    <motion.p 
                            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-gray-100"
                            initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                    >
                            OSOPCoding by Online School of Programming is India's premium programming institute offers "Zero Error Programming" training courses, 
                            With a mission to transform students into skilled IT Resource. Helping IT aspirants to build successful careers in Information Technology.
                    </motion.p>

                        <motion.div 
                            className="flex flex-col sm:flex-row justify-center gap-6 mb-16"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <Link 
                                to="/courses" 
                                className="btn-hero bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                            >
                                <BookOpen className="w-6 h-6 mr-3" />
                                Explore Courses
                            </Link>
                            <Link 
                                to="/placement" 
                                className="btn-hero bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                            >
                                <Trophy className="w-6 h-6 mr-3" />
                                View Placements
                            </Link>
                        </motion.div>

                        {/* Quick Stats */}
                        <motion.div 
                            className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-4xl mx-auto"
                            variants={staggerContainer}
                            initial="initial"
                            animate="animate"
                        >
                            <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                                <div className="text-4xl font-bold text-yellow-400 mb-2">26+</div>
                                <div className="text-gray-200">Years Of Training & Consulting</div>
                            </motion.div>
                            <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                                <div className="text-4xl font-bold text-green-400 mb-2">15K+</div>
                                <div className="text-gray-200">Trained in Classroom</div>
                            </motion.div>
                            <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                                <div className="text-4xl font-bold text-purple-400 mb-2">100%</div>
                                <div className="text-gray-200">Satisfied Learners</div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="container-max py-20 space-y-32">

                {/* Why Choose OSOP - Core Benefits */}
                <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-20 rounded-3xl relative overflow-hidden">
                    {/* Background decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full blur-2xl"></div>
                    
                    <motion.div 
                        className="text-center mb-16 relative z-10"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
                            Why Choose OSOPCoding?
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            Discover the unique features that make OSOPCoding the preferred choice for IT Training
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        <FeatureCard 
                            icon={Zap}
                            title="Industry-Ready Skills"
                            description="Learn the latest technologies and frameworks that companies actually use. Our curriculum is constantly updated based on industry demands."
                            gradient="bg-gradient-to-br from-yellow-500 to-orange-500"
                            delay={0}
                        />
                        <FeatureCard 
                            icon={Target}
                            title="100% Placement Focus"
                            description="Every course is designed with placement in mind. We don't just teach programming - we prepare you for successful careers."
                            gradient="bg-gradient-to-br from-green-500 to-emerald-500"
                            delay={0.1}
                        />
                        <FeatureCard 
                            icon={Users}
                            title="Expert Mentorship"
                            description="Learn from industry veterans with 20+ years of experience. Get personalized guidance and career counseling."
                            gradient="bg-gradient-to-br from-blue-500 to-indigo-500"
                            delay={0.2}
                        />
                        <FeatureCard 
                            icon={Globe}
                            title="26 Years Legacy"
                            description="Started in 1996, OSOP Trained 15000+ students in our classroom centers in indore."
                            gradient="bg-gradient-to-br from-purple-500 to-pink-500"
                            delay={0.3}
                        />
                        <FeatureCard 
                            icon={Clock}
                            title="Flexible Learning"
                            description="Learn at your own pace with 24/7 access to course materials online. Perfect for working professionals and students."
                            gradient="bg-gradient-to-br from-teal-500 to-cyan-500"
                            delay={0.4}
                        />
                        <FeatureCard 
                            icon={Shield}
                            title="Lifetime Support"
                            description="Get lifetime access to our Alumni network, get regular updates and career support your entire journey."
                            gradient="bg-gradient-to-br from-red-500 to-pink-500"
                            delay={0.5}
                        />
                    </div>
                </section>

               

                {/* Learning Methodology */}
                <section className="bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-20 rounded-3xl relative overflow-hidden">
                    {/* Background decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-400/10 to-purple-400/10 rounded-full blur-2xl"></div>
                    
                    <motion.div 
                        className="text-center mb-16 relative z-10"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl mb-6 shadow-lg">
                            <BookOpen className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                            Zero Error Coding Methodology
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            OSOPCoding delivers Zero Error Coding concepts in classroom and online live classes, makes student conceptualy too strong
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        <motion.div 
                            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-100 dark:border-gray-700"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                                    <span className="text-white font-bold text-xl">1</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Strong Foundation</h3>
                            </div>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                Start with programming fundamentals. We ensure you understand all important core concepts before moving to advanced topics.
                            </p>
                        </motion.div>

                        <motion.div 
                            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-100 dark:border-gray-700"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                                    <span className="text-white font-bold text-xl">2</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Project-Based Learning</h3>
                            </div>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                Build real-world projects that you can showcase to employers. Learn by doing, not just by reading.
                            </p>
                        </motion.div>

                        <motion.div 
                            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-100 dark:border-gray-700"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                                    <span className="text-white font-bold text-xl">3</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Mentor from Tech Companies</h3>
                            </div>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                Get guidance from professionals working at top tech companies. Learn industry best practices and current trends.
                            </p>
                        </motion.div>

                        <motion.div 
                            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-100 dark:border-gray-700"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                        >
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                                    <span className="text-white font-bold text-xl">4</span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Get Job Ready</h3>
                            </div>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                Mock Interviews, resume building, and Alumni support to ensure your success in the job market.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Enhanced Expert Mentors Section */}
                <section>
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">🌟 Expert Mentors</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Learn from experienced trainers who have shaped 15000+ successful careers in technology
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-3 gap-8">
                            <MentorCard 
                            name="Dr. Dharmendra Bajpai"
                            title="Chief Mentor"
                            points={["Programming Foundation Expert", "C/C++/Java/Python","Data Structures & Algorythm", "SQL"]}
                            quote="Zero Error Programming Guru"
                            experience="25+ Years"
                            students="15,000+"
                            achievements={[
                                "15000+ trained in classroom",
                                "PHD Awarded",
                                "Founded 'Kampushala' free computer education",
                                "Founded Proton Foundation School",
                                "Founded CivilJobs Academy for UPSC & MPPSC"
                            ]}
                            delay={0}
                            />
                            <MentorCard 
                                name="Manish Bajpai"
                            title="Senior Mentor"
                            points={["Data Architect", "System Design", "Senior Data Engineer","Python Data Analysis", "Snowflake/SQL", "Cloud"]}
                            quote="20+ Years IT Experience"
                            experience="15+ Years"
                            students="10,000+"
                            achievements={[
                                "Lead Onsite Team at Cincinnati, USA",
                                "Executed Client Project at Chicago, USA",
                                "SPOT Award from Synechron Technologies",
                                "Founder, Firstlogic Technologies",
                            ]}
                            delay={0.1}
                            />
                            <MentorCard 
                                name="Vaibhav Jain"
                                title="Senior Mentor AI"
                            points={["Data Science Expert", "AI/ML Specialist", "Python Expert", "Agentic AI"]}
                            quote="AI in Action"
                            bio="Expert in Data Science and Machine Learning. Helps students transition into high-paying data science roles."
                           
                            achievements={[
                                "AI research at top tech companies",
                                "Created AI Agents"
                            ]}
                            delay={0.2}
                            />
                        </div>
                </section>

                {/* Real Feedback Section */}
                <section className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/10 dark:to-primary-800/10 py-20 rounded-3xl">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                          Success Stories
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                           Watch, How OSOPCoding Helped Students in Cracking their Dream IT MNC
                        </p>
                    </motion.div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {videoTestimonials.slice(0, showAllVideos ? videoTestimonials.length : 4).map((testimonial, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 h-full flex flex-col cursor-pointer"
                                    onClick={() => openVideoModal(testimonial)}
                                >
                                    <div className="relative aspect-video bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex-shrink-0 overflow-hidden">
                                        {/* Video Thumbnail */}
                                        {testimonial.thumbnail ? (
                                            <img 
                                                src={testimonial.thumbnail} 
                                                alt={`${testimonial.name} testimonial thumbnail`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    // Fallback to gradient background if thumbnail fails to load
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center">
                                                <div className="text-center">
                                                    <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                                                        <Play className="w-6 h-6 text-primary-600" />
                                                    </div>
                                                    <p className="text-sm text-primary-600 font-medium">Video Coming Soon</p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Play Button Overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <div className="w-16 h-16 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-110 shadow-2xl">
                                                <Play className="w-6 h-6 text-white ml-1" />
                                            </div>
                                        </div>
                                        
                                        {/* Duration overlay */}
                                        {testimonial.videoUrl !== "#" && (
                                            <div className="absolute bottom-3 right-3 bg-black/80 text-white px-2 py-1 rounded text-xs font-medium">
                                                Video
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 flex-grow flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
                                                {testimonial.name}
                                            </h3>
                                            <p className="text-primary-600 dark:text-primary-400 text-sm font-medium mb-3 line-clamp-1">
                                                {testimonial.company}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        
                        {/* Show All Button */}
                        {videoTestimonials.length > 4 && (
                            <motion.div 
                                className="text-center mt-12"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                <button
                                    onClick={() => setShowAllVideos(!showAllVideos)}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
                                >
                                    {showAllVideos ? (
                                        <>
                                            <span>Show Less</span>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                        </>
                                    ) : (
                                        <>
                                            <span>Show All Videos</span>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </>
                                    )}
                                </button>
                            </motion.div>
                        )}
                    </div>
                </section>

                {/* Video Modal */}
                {videoModal.isOpen && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10010] flex items-center justify-center p-2 sm:p-4" style={{ paddingTop: '3rem' }}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white dark:bg-gray-900 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl w-full h-full max-w-6xl max-h-[90vh] sm:max-h-[85vh] flex flex-col"
                        >
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                                <div>
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                                        {videoModal.studentName}
                                    </h3>
                                    <p className="text-primary-600 dark:text-primary-400 font-medium text-sm sm:text-base">
                                        {videoModal.company}
                                    </p>
                                </div>
                                <button
                                    onClick={closeVideoModal}
                                    className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                                    aria-label="Close video"
                                >
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Video Container - Flexible height */}
                            <div className="flex-1 bg-black min-h-0 flex items-center justify-center">
                                <div className="w-full h-full flex items-center justify-center">
                                    <iframe
                                        src={videoModal.videoUrl}
                                        title={`${videoModal.studentName} - ${videoModal.company} Testimonial`}
                                        className="w-full h-full max-w-full max-h-full"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                                        allowFullScreen
                                        style={{ minHeight: '300px' }}
                                        onError={(e) => {
                                            console.error('YouTube iframe error:', e);
                                        }}
                                    ></iframe>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                        Real feedback from our successful student
                                    </div>
                                    <button
                                        onClick={closeVideoModal}
                                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Alumni Section */}
                <section>
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Our Alumni
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                            Meet our successful alumni who have secured good positions at leading companies across world
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {/* Combine success stories and placements, with success stories first */}
                        {[...successStories, ...placements.slice(0, visiblePlacements)].map((item, index) => (
                            <motion.div
                                key={`combined-${index}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.05 }}
                                className="bg-white dark:bg-gray-800 rounded-xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 overflow-hidden group hover:-translate-y-2"
                            >
                                <div className="relative overflow-hidden">
                                    <img 
                                        src={item.image} 
                                        alt={item.name} 
                                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    {/* Fallback for broken images */}
                                    <div className="hidden w-full h-64 bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                        <div className="text-gray-500 dark:text-gray-400 text-center">
                                            <div className="w-16 h-16 bg-gray-300 dark:bg-gray-500 rounded-full flex items-center justify-center mx-auto mb-2">
                                                <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                                                    {item.name.split(' ').map(n => n[0]).join('')}
                                                </span>
                                            </div>
                                            <span className="text-sm">{item.name}</span>
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300 line-clamp-1">
                                        {item.name}
                                    </h3>
                                    <div className="space-y-2">
                                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                                            <Building className="w-3 h-3 mr-1 text-primary-500 flex-shrink-0" />
                                            <span className="font-medium text-xs line-clamp-1">{item.company}</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {visiblePlacements < placements.length && (
                        <div className="text-center mt-12">
                            <motion.button
                                onClick={loadMore}
                                className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center transition-all duration-300"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Users className="w-6 h-6 mr-3" />
                                All Alumni
                                <ArrowRight className="w-6 h-6 ml-3" />
                            </motion.button>
                        </div>
                    )}
                </section>

                {/* Get Certificate Section */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Image */}
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="relative"
                            >
                                <div className="relative bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 transform rotate-1 sm:rotate-2 hover:rotate-0 transition-transform duration-500">
                                    {/* Rishi Vyas Data Analysis Image */}
                                    <div className="relative overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer group" onClick={openCertificateModal}>
                                        <img 
                                            src={rishiVyasImage} 
                                            alt="Rishi Vyas Data Analysis"
                                            className="w-full h-auto object-cover rounded-xl sm:rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105"
                                        />
                                        {/* Zoom overlay */}
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-xl sm:rounded-2xl flex items-center justify-center">
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                                                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Decorative Elements */}
                                <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce"></div>
                                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-blue-400 rounded-full animate-pulse"></div>
                                <div className="absolute top-1/2 -right-6 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
                            </motion.div>

                            {/* Right Side - Content */}
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="space-y-6"
                            >
                                <div>
                                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium">
                                        🏆 Get Certified 
                                    </span>
                                </div>

                                <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
                                    OSOP Certified IT Professional
                                </h2>

                                <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                                    Get industry recognized Certificate on completion of courses. Certified from OSOP Means the "Guarantee of Skills".
                                </p>

                                {/* OSOP Coding Certificate Features */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 shadow-lg border border-blue-200 dark:border-blue-800">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <Award className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                                        OSOP Coding Certificates
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Industry-Recognized</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Certificates recognized by IT companies worldwide</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Project-Based Learning</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Real projects included with every certification course</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <CheckCircle className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">26+ Years Excellence</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Proven track record of 15K+ IT Professional trained in classroom</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* CTA Button */}
                                <div className="pt-4">
                                    <Link 
                                        to="/enquire"
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 inline-flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                                    >
                                        <span>Get Certified Today</span>
                                        <ArrowRight className="w-5 h-5" />
                                    </Link>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Enhanced Contact Information */}
                {/* <section>
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">📞 Get In Touch</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Ready to start your programming journey? Contact us today and take the first step towards your dream career!
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                       <ContactInfoCard 
                           title="Corporate Head Office"
                            icon={<Briefcase className="w-6 h-6 text-white"/>}
                            address="First Floor, Kohinoor Tower, Opp HDFC Bank, Rajmohalla Indore, Madhya Pradesh 452001"
                           phone="+91 76490 98000"
                           email="info@osopcoding.com"
                            hours="Mon - Sat: 9:00 AM - 8:00 PM"
                            delay={0}
                       />
                       <ContactInfoCard 
                           title="Training Center"
                            icon={<Code className="w-6 h-6 text-white"/>}
                            address="2nd Floor, Sundaram Complex, Bhanwarkua Tower Square Road, Indore, Madhya Pradesh 452014"
                           phone="+91 76490 99000"
                            email="training@osopcoding.com"
                            hours="Mon - Sat: 10:00 AM - 7:00 PM"
                            delay={0.1}
                       />
                    </div>
                </section> */}
                
                {/* Enhanced Call to Action */}
                <section className="text-center bg-gradient-professional p-16 rounded-3xl shadow-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
                    <div className="relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Want to Transform Your Career?
                            </h2>
                            <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                                Join india's most premium coding courses from OSOPCoding and build strong career in IT. 
                                Join OSOPCoding for "Classroom jaisi learning".
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-6">
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link 
                                        to="/courses" 
                                        className="btn-hero bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                    >
                                        <BookOpen className="w-6 h-6 mr-3" />
                                💻 View Programming Courses
                                        <ArrowRight className="w-6 h-6 ml-3" />
                            </Link>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link 
                                        to="/enquire" 
                                        className="btn-hero bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                    >
                                        <GraduationCap className="w-6 h-6 mr-3" />
                               🎓 Enroll Now
                                        <ArrowRight className="w-6 h-6 ml-3" />
                            </Link>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>

            {/* Certificate Modal */}
            <CertificateModal
              isOpen={certificateModal.isOpen}
              onClose={closeCertificateModal}
              imageSrc={rishiVyasImage}
              imageAlt="Rishi Vyas Data Analysis Certificate"
            />
        </div>
    );
};

export default WhyOsop;